import { useState, useMemo } from "react";
import { Link } from "react-router";
import { useApp } from "../context/AppContext";
import BpsLogo from "../components/BpsLogo";
import {
  NotebookPen, Clock, BookMarked, Star, TriangleAlert,
  ExternalLink, Globe, Users, ClipboardCheck, ChevronRight,
  Phone, CheckCircle, MessageCircleQuestion, MonitorPlay, X,
  FileBadge2
} from "lucide-react";

export default function Home() {
  const { visitors } = useApp();
  const { todayVisitors, selesaiHariIni, antrianAktif, sedangDilayani, avgRating } = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    const todayVisitors  = visitors.filter(v => v.timestamp.startsWith(today));
    const selesaiHariIni = todayVisitors.filter(v => v.status === "Selesai");
    const antrianAktif   = visitors.filter(v => v.status === "Menunggu");
    const sedangDilayani = visitors.filter(v => v.status === "Dilayani");
    const ratings        = visitors.filter(v => v.rating).map(v => v.rating!);
    const avgRating      = ratings.length
      ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
      : "0";
    return { todayVisitors, selesaiHariIni, antrianAktif, sedangDilayani, avgRating };
  }, [visitors]);

  // ID unik visitor ini (UUID, disimpan saat daftar buku tamu)
  const [myId, setMyId] = useState<string | null>(() => {
    const raw = localStorage.getItem("pst_my_antrian");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      // Auto-clear jika beda hari — nomor antrian reset harian, UUID lama tidak valid
      if (parsed.date !== new Date().toISOString().split("T")[0]) {
        localStorage.removeItem("pst_my_antrian");
        return null;
      }
      return parsed.id ?? null;
    } catch {
      // Format lama (plain integer dari versi sebelumnya) — hapus dan abaikan
      localStorage.removeItem("pst_my_antrian");
      return null;
    }
  });

  // Lookup by UUID — dijamin unik, tidak tertukar dengan nomor antrian hari lain
  const myVisitor = myId ? visitors.find(v => v.id === myId) : null;

  // Hapus status setelah selesai + dinilai
  const clearMyAntrian = () => {
    localStorage.removeItem("pst_my_antrian");
    setMyId(null);
  };

  const layanan = [
    { icon: FileBadge2, title: "Rekomendasi Statistik", desc: "Permohonan rekomendasi data statistik resmi untuk kebutuhan instansi Anda", color: "bg-blue-600" },
    { icon: MessageCircleQuestion, title: "Konsultasi Statistik", desc: "Konsultasi data dan metodologi statistik bersama ahli BPS Lombok Tengah", color: "bg-emerald-600" },
    { icon: BookMarked, title: "Layanan Perpustakaan", desc: "Akses referensi dan publikasi statistik melalui perpustakaan digital BPS", color: "bg-purple-600" },
  ];

  const stats = [
    { label: "Pengunjung Hari Ini", value: String(todayVisitors.length), icon: Users, color: "text-blue-600" },
    { label: "Layanan Selesai", value: String(selesaiHariIni.length), icon: CheckCircle, color: "text-emerald-600" },
    { label: "Rata-rata Kepuasan", value: `${avgRating}★`, icon: Star, color: "text-amber-500" },
    { label: "Antrian Aktif", value: String(antrianAktif.length), icon: Clock, color: "text-orange-500" },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">

      {/* === STATUS ANTRIAN SAYA === */}
      {myVisitor && (
        <div className={`rounded-2xl p-5 shadow-lg border-2 ${
          myVisitor.status === "Dilayani"
            ? "bg-green-50 border-green-400"
            : myVisitor.status === "Selesai"
            ? "bg-gray-50 border-gray-300"
            : "bg-blue-50 border-blue-300"
        }`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 mb-1">ANTRIAN ANDA</p>
              <div className="flex items-center gap-3">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-black ${
                  myVisitor.status === "Dilayani" ? "bg-green-500" :
                  myVisitor.status === "Selesai"  ? "bg-gray-400" : "bg-[#003580]"
                }`}>
                  {String(myVisitor.nomorAntrian).padStart(3, "0")}
                </div>
                <div>
                  <p className="font-bold text-gray-800">{myVisitor.nama}</p>
                  <p className="text-xs text-gray-500">{myVisitor.jenisLayanan}</p>
                  <div className="mt-1">
                    {myVisitor.status === "Menunggu" && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                        <Clock size={11} /> Menunggu giliran...
                      </span>
                    )}
                    {myVisitor.status === "Dilayani" && (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-200 px-2 py-0.5 rounded-full animate-pulse">
                        ✅ Anda Sedang Dilayani!
                      </span>
                    )}
                    {myVisitor.status === "Selesai" && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                        <CheckCircle size={11} /> Layanan Selesai
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {myVisitor.status === "Dilayani" && myVisitor.petugasNama && (
                <div className="mt-3 bg-green-600 text-white rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-base font-black flex-shrink-0">
                    {myVisitor.petugasNama.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-semibold opacity-80 leading-none mb-0.5">Anda dilayani oleh</p>
                    <p className="text-base font-black leading-tight">{myVisitor.petugasNama}</p>
                    <p className="text-xs opacity-75 mt-0.5">Silakan menuju meja petugas 👋</p>
                  </div>
                </div>
              )}
              {myVisitor.status === "Selesai" && (
                <div className="flex gap-2 mt-3">
                  <Link
                    to={`/penilaian?no=${myVisitor.nomorAntrian}`}
                    className="flex-1 text-center text-xs font-bold bg-amber-500 text-white py-2 rounded-xl hover:bg-amber-600 transition-colors"
                  >
                    ⭐ Nilai Layanan
                  </Link>
                  <button
                    onClick={clearMyAntrian}
                    className="text-xs text-gray-400 bg-gray-100 px-3 py-2 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Tutup
                  </button>
                </div>
              )}
            </div>
            {myVisitor.status === "Menunggu" && (
              <button onClick={clearMyAntrian} className="text-gray-300 hover:text-gray-500 p-1">
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#003580] to-[#0057b7] text-white rounded-2xl p-6 text-center shadow-lg">
        <div className="mx-auto mb-3 flex items-center justify-center">
          <BpsLogo size={64} />
        </div>
        <h1 className="text-xl" style={{fontWeight:700}}>Sistem Pelayanan Statistik Terpadu</h1>
        <h2 className="text-base opacity-90">BPS Kabupaten Lombok Tengah</h2>
        <p className="text-sm opacity-75 mt-2">Pelayanan yang cepat, akurat, dan terpercaya</p>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <Link to="/buku-tamu" className="bg-white text-[#003580] rounded-xl py-3 px-4 flex flex-col items-center gap-1 hover:bg-blue-50 transition-colors shadow-md">
            <NotebookPen size={22} />
            <span className="text-sm" style={{fontWeight:700}}>Daftar Kunjungan</span>
            <span className="text-xs opacity-70">Isi buku tamu</span>
          </Link>
          <a href="https://pst.bps.go.id" target="_blank" rel="noopener noreferrer"
            className="bg-white/20 text-white border border-white/30 rounded-xl py-3 px-4 flex flex-col items-center gap-1 hover:bg-white/30 transition-colors">
            <BookMarked size={22} />
            <span className="text-sm" style={{fontWeight:700}}>Perpustakaan Online</span>
            <span className="text-xs opacity-70">pst.bps.go.id</span>
          </a>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <s.icon size={20} className={s.color} />
            <div className={`text-xl mt-1 ${s.color}`} style={{fontWeight:700}}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* === PANEL ANTRIAN REALTIME === */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-[#003580] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm font-bold">Antrian Realtime</span>
          </div>
          <Link to="/antrian" className="flex items-center gap-1 text-xs text-white/70 hover:text-white transition-colors">
            <MonitorPlay size={14} />
            <span>Tampilan Monitor</span>
          </Link>
        </div>

        <div className="p-4">
          {/* Sedang dilayani */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-400 mb-2">SEDANG DILAYANI</p>
            {sedangDilayani.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {sedangDilayani.map(v => (
                  <div key={v.id} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                    <span className="text-lg font-black text-blue-700">
                      {String(v.nomorAntrian).padStart(3, "0")}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-blue-800 leading-tight">{v.nama}</p>
                      <p className="text-xs text-blue-500">{v.jenisLayanan.split(" ")[0]}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Belum ada yang dilayani</p>
            )}
          </div>

          {/* Antrian menunggu */}
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-2">
              MENUNGGU <span className="text-orange-500">({antrianAktif.length})</span>
            </p>
            {antrianAktif.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {antrianAktif.slice(0, 10).map((v, i) => (
                  <div key={v.id} className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold border-2 ${
                    i === 0 ? "border-orange-400 bg-orange-50 text-orange-700" : "border-gray-200 bg-gray-50 text-gray-600"
                  }`}>
                    {String(v.nomorAntrian).padStart(3, "0")}
                  </div>
                ))}
                {antrianAktif.length > 10 && (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xs font-bold bg-gray-100 text-gray-400 border-2 border-gray-200">
                    +{antrianAktif.length - 10}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Tidak ada antrian</p>
            )}
          </div>
        </div>
      </div>

      {/* Jenis Layanan */}
      <div>
        <h3 className="mb-3" style={{color:"#003580"}}>Jenis Layanan Tersedia</h3>
        <div className="space-y-3">
          {layanan.map((l, i) => (
            <Link key={i} to="/buku-tamu"
              className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all">
              <div className={`${l.color} text-white p-2.5 rounded-xl flex-shrink-0`}>
                <l.icon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm" style={{fontWeight:600, color:"#003580"}}>{l.title}</div>
                <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{l.desc}</div>
              </div>
              <ChevronRight size={16} className="text-gray-400 flex-shrink-0 mt-1" />
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/pengaduan" className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-3 hover:bg-red-100 transition-colors">
          <TriangleAlert size={20} className="text-red-500" />
          <div>
            <div className="text-sm" style={{fontWeight:600, color:"#dc2626"}}>Pengaduan</div>
            <div className="text-xs text-red-400">Sampaikan keluhan</div>
          </div>
        </Link>
        <Link to="/penilaian" className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-3 hover:bg-amber-100 transition-colors">
          <Star size={20} className="text-amber-500" />
          <div>
            <div className="text-sm" style={{fontWeight:600, color:"#d97706"}}>Penilaian</div>
            <div className="text-xs text-amber-400">Nilai petugas</div>
          </div>
        </Link>
        <a href="https://s.bps.go.id/skd_bpsloteng" target="_blank" rel="noopener noreferrer"
          className="col-span-2 bg-green-50 border border-green-100 rounded-xl p-4 flex items-center gap-3 hover:bg-green-100 transition-colors">
          <ClipboardCheck size={20} className="text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm" style={{fontWeight:600, color:"#16a34a"}}>Survei Kebutuhan Data</div>
            <div className="text-xs text-green-500">Bantu kami tingkatkan layanan statistik</div>
          </div>
          <ExternalLink size={14} className="text-green-400 flex-shrink-0" />
        </a>
      </div>

      {/* External Links */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-3">
        <h4 className="text-gray-700">Tautan Penting</h4>
        <a href="https://lomboktengahkab.bps.go.id" target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-between py-2.5 px-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-blue-600" strokeWidth={2} />
            <span className="text-sm text-blue-700" style={{fontWeight:500}}>Website Resmi BPS Lombok Tengah</span>
          </div>
          <ExternalLink size={14} className="text-blue-400" />
        </a>
        <a href="https://pst.bps.go.id" target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-between py-2.5 px-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
          <div className="flex items-center gap-2">
            <BookMarked size={16} className="text-purple-600" />
            <span className="text-sm text-purple-700" style={{fontWeight:500}}>Perpustakaan Online BPS (pst.bps.go.id)</span>
          </div>
          <ExternalLink size={14} className="text-purple-400" />
        </a>
      </div>

      {/* Contact */}
      <div className="bg-[#003580] text-white rounded-xl p-4 space-y-2">
        <h4 className="opacity-90">Waktu Layanan Kunjungan Langsung</h4>
        <div className="flex items-center gap-2 text-sm opacity-80">
          <Clock size={14} className="flex-shrink-0" />
          <div>
            <div>Senin – Kamis: 08.30 – 15.30 WITA</div>
            <div>Jumat: 08.30 – 15.30 WITA</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm opacity-80">
          <Phone size={14} />
          <span>email: bps5202@bps.go.id | halosemeton: 087797500640</span>
        </div>
      </div>
    </div>
  );
}
